import type { CertInfo } from './sign-types';
import { toIsoDate } from './sign-utils';
import type { SignProfile } from './sign-types';

declare global {
  interface Window {
    cadesplugin?: CadesPlugin;
  }
}

type AsyncSpawn = (worker: () => Generator<unknown, void, unknown>) => void;
type CadesGenerator<T> = Generator<unknown, T, unknown>;

type CadesStore = {
  Open(location: number, name: string, mode: number): Promise<void>;
  readonly Certificates: Promise<CadesCertificates>;
  Close(): Promise<void>;
};

type CadesCertificates = {
  readonly Count: Promise<number>;
  Item(index: number): Promise<CadesCertificate>;
  Find(findType: number, value: string): Promise<CadesCertificates>;
};

type CadesCertificate = {
  HasPrivateKey(): Promise<boolean>;
  readonly Thumbprint: Promise<string>;
  readonly SubjectName: Promise<string>;
  readonly IssuerName: Promise<string>;
  readonly SerialNumber: Promise<string>;
  readonly ValidFromDate: Promise<string>;
  readonly ValidToDate: Promise<string>;
};

type CadesHashedData = {
  propset_Algorithm(algorithm: number): Promise<void>;
  propset_DataEncoding(encoding: number): Promise<void>;
  Hash(value: string): Promise<void>;
  readonly Value: Promise<string>;
};

type CadesSigner = {
  propset_Certificate(cert: CadesCertificate): Promise<void>;
  propset_Options(options: number): Promise<void>;
  propset_CheckCertificate?(check: boolean): Promise<void>;
};

type CadesSignedData = {
  propset_ContentEncoding(encoding: number): Promise<void>;
  propset_Content(content: string): Promise<void>;
  SignCades(signer: CadesSigner, type: number, detached: boolean): Promise<string>;
};

type CadesPlugin = {
  async_spawn: AsyncSpawn;
  CreateObjectAsync(objectName: string): Promise<unknown>;
  getLastError(error: unknown): string;
  CAPICOM_CERTIFICATE_FIND_SHA1_HASH: number;
  CAPICOM_CERTIFICATE_INCLUDE_WHOLE_CHAIN: number;
  CADESCOM_BASE64_TO_BINARY: number;
  CADESCOM_CADES_BES: number;
  CADESCOM_CADES_X_LONG_TYPE_1?: number;
  CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256: number;
};

const CERTIFICATES_UNAVAILABLE_MESSAGE =
  'Сертификаты электронной подписи не найдены. Убедитесь, что КриптоПро CSP и браузерный плагин установлены, а носитель ключа (токен/смарт-карта) подключён к компьютеру.';

export class CryptoProGateway {
  constructor(private readonly initialPlugin?: CadesPlugin) {}

  async checkAvailability(): Promise<void> {
    await this.run(function* availabilityCheck(): CadesGenerator<void> {
      return undefined;
    });
  }

  async getCertificates(): Promise<CertInfo[]> {
    return this.run(
      function* getCertificatesWorker(this: CryptoProGateway): CadesGenerator<CertInfo[]> {
        const plugin = this.getPlugin();
        const store = (yield plugin.CreateObjectAsync('CAdESCOM.Store')) as CadesStore;
        yield store.Open(2, 'My', 2);

        const certs = (yield store.Certificates) as CadesCertificates;
        const count = (yield certs.Count) as number;
        const result: CertInfo[] = [];

        for (let i = 1; i <= count; i += 1) {
          const cert = (yield certs.Item(i)) as CadesCertificate;
          const hasPrivateKey = (yield cert.HasPrivateKey()) as boolean;
          if (!hasPrivateKey) {
            continue;
          }

          result.push({
            thumbprint: (yield cert.Thumbprint) as string,
            subjectName: (yield cert.SubjectName) as string,
            issuerName: (yield cert.IssuerName) as string,
            serialNumber: (yield cert.SerialNumber) as string,
            validFrom: toIsoDate((yield cert.ValidFromDate) as string),
            validTo: toIsoDate((yield cert.ValidToDate) as string),
          });
        }

        yield store.Close();
        return result;
      }.bind(this),
    );
  }

  async hashBase64(base64Data: string): Promise<string> {
    return this.run(
      function* hashWorker(this: CryptoProGateway): CadesGenerator<string> {
        const plugin = this.getPlugin();
        const hashedData = (yield plugin.CreateObjectAsync(
          'CAdESCOM.HashedData',
        )) as CadesHashedData;
        yield hashedData.propset_Algorithm(plugin.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256);
        yield hashedData.propset_DataEncoding(plugin.CADESCOM_BASE64_TO_BINARY);
        yield hashedData.Hash(base64Data);
        return (yield hashedData.Value) as string;
      }.bind(this),
    );
  }

  async signDetached(
    base64Data: string,
    thumbprint: string,
    profile: SignProfile,
  ): Promise<string> {
    return this.run(
      function* signWorker(this: CryptoProGateway): CadesGenerator<string> {
        const plugin = this.getPlugin();
        const store = (yield plugin.CreateObjectAsync('CAdESCOM.Store')) as CadesStore;
        yield store.Open(2, 'My', 2);

        const certs = (yield store.Certificates) as CadesCertificates;
        const found = (yield certs.Find(
          plugin.CAPICOM_CERTIFICATE_FIND_SHA1_HASH,
          thumbprint,
        )) as CadesCertificates;
        const count = (yield found.Count) as number;
        if (count < 1) {
          throw new Error('Выбранный сертификат не найден в хранилище');
        }

        const cert = (yield found.Item(1)) as CadesCertificate;
        const signer = (yield plugin.CreateObjectAsync('CAdESCOM.CPSigner')) as CadesSigner;
        yield signer.propset_Certificate(cert);
        yield signer.propset_Options(plugin.CAPICOM_CERTIFICATE_INCLUDE_WHOLE_CHAIN);

        const signedData = (yield plugin.CreateObjectAsync(
          'CAdESCOM.CadesSignedData',
        )) as CadesSignedData;
        yield signedData.propset_ContentEncoding(plugin.CADESCOM_BASE64_TO_BINARY);
        yield signedData.propset_Content(base64Data);

        const signature = (yield signedData.SignCades(
          signer,
          resolveCadesType(plugin, profile),
          true,
        )) as string;

        yield store.Close();
        return signature;
      }.bind(this),
    );
  }

  private async run<T>(operation: () => CadesGenerator<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const plugin = this.getPlugin();
      if (
        typeof plugin.async_spawn !== 'function' ||
        typeof plugin.CreateObjectAsync !== 'function'
      ) {
        reject(new Error(CERTIFICATES_UNAVAILABLE_MESSAGE));
        return;
      }
      plugin.async_spawn(
        function* spawnWrapper(this: CryptoProGateway): CadesGenerator<void> {
          try {
            const result = yield* operation();
            resolve(result);
          } catch (error) {
            const message = safePluginError(this.getPluginOrUndefined(), error);
            reject(new Error(message));
          }
        }.bind(this),
      );
    });
  }

  private getPlugin(): CadesPlugin {
    const plugin = this.getPluginOrUndefined();
    if (!plugin || typeof plugin.CreateObjectAsync !== 'function') {
      throw new Error(CERTIFICATES_UNAVAILABLE_MESSAGE);
    }
    return plugin;
  }

  private getPluginOrUndefined(): CadesPlugin | undefined {
    return this.initialPlugin ?? window.cadesplugin;
  }
}

function resolveCadesType(plugin: CadesPlugin, profile: SignProfile): number {
  if (profile === 'CAdES-X-Long-Type-1') {
    if (typeof plugin.CADESCOM_CADES_X_LONG_TYPE_1 === 'number') {
      return plugin.CADESCOM_CADES_X_LONG_TYPE_1;
    }
    throw new Error('Профиль CAdES-X-Long-Type-1 не поддерживается в текущей версии плагина');
  }
  return plugin.CADESCOM_CADES_BES;
}

function safePluginError(plugin: CadesPlugin | undefined, error: unknown): string {
  try {
    const rawMessage = plugin?.getLastError(error) ?? String(error);
    return sanitizePluginError(rawMessage);
  } catch {
    return sanitizePluginError(String(error));
  }
}

function sanitizePluginError(message: string): string {
  // Убираем технические коды вида "(0x8010006E)" из сообщений для пользователя.
  return message.replace(/\s*\(0x[0-9a-f]+\)/gi, '').trim();
}
